import React, { useState, useEffect } from 'react';
import { ideaApi, Idea } from '../services/api';
import './LinkedIdeas.css';

interface LinkedIdeaData {
  id: number;
  source_id: number;
  target_id: number;
  strength: number;
  type: string;
  reason: string;
  content: string;
  summary?: string;
  category?: string;
  created_at: string;
}

interface LinkedIdeasProps {
  ideaId: number;
  onIdeaClick: (idea: Idea) => void;
}

export const LinkedIdeas: React.FC<LinkedIdeasProps> = ({ ideaId, onIdeaClick }) => {
  const [linkedIdeas, setLinkedIdeas] = useState<LinkedIdeaData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    fetchLinkedIdeas();
  }, [ideaId]);

  const fetchLinkedIdeas = async () => {
    setIsLoading(true);
    try {
      const response = await ideaApi.getLinkedIdeas(ideaId);
      setLinkedIdeas(response);
    } catch (error) {
      console.error('Failed to fetch linked ideas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'semantic':
        return '🧠';
      case 'temporal':
        return '⏱️';
      case 'category':
        return '📁';
      case 'tag':
        return '🏷️';
      default:
        return '🔗';
    }
  };

  const getStrengthClass = (strength: number) => {
    if (strength >= 0.8) return 'strength-high';
    if (strength >= 0.6) return 'strength-medium';
    return 'strength-low';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString();
  };

  if (linkedIdeas.length === 0) {
    return null;
  }

  const displayedIdeas = isExpanded ? linkedIdeas : linkedIdeas.slice(0, 3);

  return (
    <div className="linked-ideas">
      <div className="linked-ideas-header">
        <h3>Related Ideas ({linkedIdeas.length})</h3>
        {linkedIdeas.length > 3 && (
          <button 
            className="expand-button"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? 'Show Less' : 'Show All'}
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="loading">Finding connections...</div>
      ) : (
        <div className="linked-ideas-list">
          {displayedIdeas.map((link) => (
            <div 
              key={`${link.source_id}-${link.target_id}-${link.type}`}
              className={`linked-idea-item ${getStrengthClass(link.strength)}`}
              onClick={() => onIdeaClick({
                id: link.id,
                content: link.content,
                summary: link.summary,
                category: link.category,
                created_at: link.created_at,
                updated_at: link.created_at,
                processed: true
              } as Idea)}
            >
              <div className="link-info">
                <span className="link-type" title={link.type}>
                  {getTypeIcon(link.type)}
                </span>
                <span className="link-strength" title={`Strength: ${(link.strength * 100).toFixed(0)}%`}>
                  <div className="strength-bar">
                    <div 
                      className="strength-fill"
                      style={{ width: `${link.strength * 100}%` }}
                    />
                  </div>
                </span>
              </div>
              
              <div className="linked-idea-content">
                <p className="idea-text">
                  {link.summary || link.content}
                </p>
                <div className="idea-meta">
                  <span className="link-reason">{link.reason}</span>
                  <span className="idea-date">{formatDate(link.created_at)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isExpanded && linkedIdeas.length > 3 && (
        <div className="more-indicator">
          +{linkedIdeas.length - 3} more related ideas
        </div>
      )}
    </div>
  );
};